/**
 * PLAT-10915 — the frontend half of an AI text generation.
 *
 * Posts identifiers and inputs to `/api/aiprompts/prompt-generate` (PLAT-10914)
 * and hands back the options. It composes NOTHING (epic decision E3): the
 * prompt record, the Brand Kit layering and the model choice all live
 * server-side, and this client never learns the prompt's text.
 *
 * Parsing lives here rather than in the endpoint (story decision DS5): the
 * endpoint returns `result: unknown` because the output contract belongs to the
 * prompt record, so turning that into `string[]` is the caller's job.
 */
import { authenticatedFetch } from '@src/utility/authenticatedFetch';
import { LogManager } from '@src/utility/Logger';

import type { AITextBaseTextScope } from '@src/components/sidepanel/CompositionSettings/types';

/** Exported so a caller's AI Prompt Log record names the route that ran it. */
export const PROMPT_GENERATE_ENDPOINT = '/api/aiprompts/prompt-generate';

/** Five options are the budget (E9/E5) — the set is replaced, never appended. */
export const MAX_AI_TEXT_OPTIONS = 5;

const logger = LogManager.getLogger('repository:AITextGeneration');

function getApiBase(): string {
  return process.env.REACT_APP_API_URL ?? '';
}

/**
 * Typed failure the control can render. Mirrors `DubbingApiError` — preserves
 * the server's `error` string so a misconfigured prompt id shows the reason
 * rather than a bare status code.
 */
export class AITextGenerationError extends Error {
  readonly status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = 'AITextGenerationError';
    this.status = status;
  }
}

export interface AITextGenerationRequest {
  /** DatoCMS `ai_prompt` record id, from the control's config. */
  promptId: string;
  /** Context BY REFERENCE — the backend resolves what it needs (E3). */
  storyId?: string;
  showcaseId?: string;
  clipId?: string;
  /** The resolved words the prompt reads, and which scope they came from. */
  baseText: string;
  baseTextScope: AITextBaseTextScope;
  /**
   * Free-text steer. PLAT-10917 is what sends a non-empty value; the parameter
   * exists here so that lands as a wiring change.
   */
  instructions?: string;
  signal?: AbortSignal;
  /**
   * PLAT-11207 — which prompt record actually ran, as the endpoint reports it.
   *
   * The response carries `promptTitle` and `promptType` and this function has
   * no use for either; the AI Prompt Log does. A Smart Graphics control knows
   * only its prompt's id, and there is no by-id prompt lookup on the client, so
   * this is the only place that identity is available without a second round
   * trip. Called as soon as the body is read — before the "no usable text"
   * failure — so a rejected generation is still logged against its prompt.
   */
  onPromptMeta?: (meta: {
    promptId?: string;
    promptTitle?: string;
    promptType?: string;
  }) => void;
}

/**
 * Run a prompt and return its options, preferred first.
 *
 * Throws `AITextGenerationError` on a non-2xx response, an unreachable API, or
 * a response that yields no usable option — every one of those is a failed
 * generation the control has to surface.
 */
export async function generateAIText(
  request: AITextGenerationRequest,
): Promise<string[]> {
  const {
    promptId,
    storyId,
    showcaseId,
    clipId,
    baseText,
    baseTextScope,
    instructions,
    signal,
    onPromptMeta,
  } = request;

  let response: Response;
  try {
    response = await authenticatedFetch(
      `${getApiBase()}${PROMPT_GENERATE_ENDPOINT}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          promptId,
          context: { storyId, showcaseId, clipId },
          inputs: {
            baseText: { scope: baseTextScope, text: baseText },
            ...(instructions ? { instructions } : {}),
          },
        }),
        signal,
      },
    );
  } catch (error) {
    throw new AITextGenerationError(
      error instanceof Error ? error.message : 'Could not reach the AI service',
    );
  }

  if (!response.ok) {
    throw new AITextGenerationError(
      await readServerError(response),
      response.status,
    );
  }

  const body = (await response.json()) as {
    result?: unknown;
    promptId?: string;
    promptTitle?: string;
    promptType?: string;
  };
  onPromptMeta?.({
    promptId: body?.promptId,
    promptTitle: body?.promptTitle,
    promptType: body?.promptType,
  });
  const options = parseAITextOptions(body?.result);
  if (options.length === 0) {
    logger.warn('AI text generation returned no usable option', { promptId });
    throw new AITextGenerationError('The model returned no usable text');
  }
  return options;
}

/**
 * Normalize whatever the prompt returned into options, preferred first (DS5).
 *
 * Tolerant on purpose — the shape is the prompt record's contract, not ours:
 * an array of strings, a JSON array the endpoint handed back as text (fenced
 * or not), an `{ options: [...] }` wrapper, or a single plain string all work.
 * A response yielding ONE usable string is a success; it just offers no
 * alternatives.
 *
 * Each option is trimmed at its ends only — options may be multi-line (E9) and
 * trimming per line would destroy a bulleted summary.
 */
export function parseAITextOptions(result: unknown): string[] {
  if (Array.isArray(result)) {
    return result
      .filter((entry): entry is string => typeof entry === 'string')
      .map(entry => entry.trim())
      .filter(entry => entry !== '')
      .slice(0, MAX_AI_TEXT_OPTIONS);
  }

  if (result && typeof result === 'object') {
    const { options } = result as { options?: unknown };
    return Array.isArray(options) ? parseAITextOptions(options) : [];
  }

  if (typeof result !== 'string') return [];

  const cleaned = stripMarkdownFence(result).trim();
  if (cleaned === '') return [];
  if (cleaned.startsWith('[') || cleaned.startsWith('{')) {
    try {
      return parseAITextOptions(JSON.parse(cleaned));
    } catch {
      // Not JSON after all — fall through and take it as the single option
      // rather than failing a generation over a formatting slip.
    }
  }
  return [cleaned];
}

/** Same tolerance `parsePromptResult` applies server-side. */
function stripMarkdownFence(raw: string): string {
  const fenced = raw.trim().match(/^```(?:[a-zA-Z]+)?\s*\n?([\s\S]*?)\n?```$/);
  return fenced ? fenced[1] : raw;
}

/**
 * The message is rendered verbatim in the control, so an unstructured body
 * (an HTML error page, a stack trace from a proxy) is truncated rather than
 * spilled into the popover. A structured `{ error }` is the server's own
 * message and passes through whole.
 */
const MAX_RAW_ERROR_CHARS = 200;

async function readServerError(response: Response): Promise<string> {
  const fallback = `AI text generation failed: ${response.status} ${response.statusText}`;
  try {
    const raw = await response.text();
    if (!raw) return fallback;
    try {
      const parsed = JSON.parse(raw) as { error?: unknown };
      if (typeof parsed.error === 'string') return parsed.error;
    } catch {
      // Not JSON — fall through to the truncated raw body.
    }
    return raw.length > MAX_RAW_ERROR_CHARS
      ? `${raw.slice(0, MAX_RAW_ERROR_CHARS)}…`
      : raw;
  } catch {
    return fallback;
  }
}
